import { useContext, useMemo } from "react";
import { isSameDay, isToday, isYesterday } from "date-fns";
import { AppContext } from "../../AppContextProvider";
import { ChatConversation } from "../../models/Chat";
import User, { Role } from "../../models/User";
import { senderInitials } from "../emails/outlookStyle";
import { base64ToUint8Array } from "../rfqs/filledFormPreview";

/** Right padding (theme spacing) that clears `RcFab` (56px, fixed 24px from the right). */
export const CHAT_FAB_GUTTER = 11;

export function userDisplayName(user: User | undefined | null): string {
  if (!user) return "Former user";
  const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return full || user.name || user.email || user.userName || `User ${user.id}`;
}

export function userInitials(user: User | undefined | null): string {
  return senderInitials(userDisplayName(user));
}

const AVATAR_COLORS = [
  "#6264a7",
  "#c4314b",
  "#0078d4",
  "#498205",
  "#ca5010",
  "#8764b8",
  "#038387",
  "#986f0b",
];

export function avatarColor(seed: number): string {
  return AVATAR_COLORS[Math.abs(seed) % AVATAR_COLORS.length];
}

export interface ChatDirectory {
  me: User;
  staff: User[];
  usersById: Map<number, User>;
}

/** Staff list and id lookup from the session; admins also receive customers, which are excluded. */
export function useChatDirectory(): ChatDirectory {
  const { user, users } = useContext(AppContext);
  return useMemo(() => {
    const byId = new Map<number, User>();
    for (const u of users) byId.set(u.id, u);
    byId.set(user.id, user);
    const staff = Array.from(byId.values())
      .filter((u) => u.role === Role.ADMIN || u.role === Role.EMPLOYEE)
      .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
    return { me: user, staff, usersById: byId };
  }, [user, users]);
}

export function conversationTitle(
  conversation: ChatConversation,
  meId: number,
  usersById: Map<number, User>
): string {
  if (conversation.type === "channel") return conversation.name || "Untitled channel";
  if (conversation.name) return conversation.name;
  const others = conversation.memberIds.filter((id) => id !== meId);
  if (others.length === 0) return userDisplayName(usersById.get(meId));
  return others.map((id) => userDisplayName(usersById.get(id))).join(", ");
}

export function directPartnerId(conversation: ChatConversation, meId: number): number | null {
  if (conversation.type !== "direct") return null;
  return conversation.memberIds.find((id) => id !== meId) ?? null;
}

export function formatChatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** Compact timestamp for the conversation list: time today, weekday this week, else date. */
export function formatListTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  if (isToday(date)) return formatChatTime(iso);
  const diffDays = (now.getTime() - date.getTime()) / 86_400_000;
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "numeric", day: "numeric" });
}

export function formatDayDivider(iso: string): string {
  const date = new Date(iso);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

export function isSameChatDay(a: string, b: string): boolean {
  return isSameDay(new Date(a), new Date(b));
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.replace(/^data:[^,]*,/, ""));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function base64ToBlob(base64: string, contentType: string): Blob {
  return new Blob([base64ToUint8Array(base64)], {
    type: contentType || "application/octet-stream",
  });
}

export function mentionToken(user: User): string {
  return `@${userDisplayName(user)}`;
}

/** Mentions are stored as ids alongside plain text; keep only ids whose `@Name` is still in the body. */
export function mentionsInBody(
  body: string,
  candidateIds: Iterable<number>,
  usersById: Map<number, User>
): number[] {
  const ids: number[] = [];
  for (const id of Array.from(new Set(candidateIds))) {
    const user = usersById.get(id);
    if (user && body.includes(mentionToken(user))) ids.push(id);
  }
  return ids;
}

export type BodySegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; text: string; userId: number }
  | { kind: "link"; text: string };

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function splitLinks(text: string): BodySegment[] {
  const segments: BodySegment[] = [];
  let last = 0;
  for (const match of Array.from(text.matchAll(URL_PATTERN))) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ kind: "text", text: text.slice(last, index) });
    segments.push({ kind: "link", text: match[0] });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ kind: "text", text: text.slice(last) });
  return segments;
}

export function segmentBody(
  body: string,
  mentionUserIds: number[],
  usersById: Map<number, User>
): BodySegment[] {
  const tokens: Array<{ id: number; token: string }> = [];
  for (const id of mentionUserIds) {
    const user = usersById.get(id);
    if (user) tokens.push({ id, token: mentionToken(user) });
  }
  tokens.sort((a, b) => b.token.length - a.token.length);

  const segments: BodySegment[] = [];
  let rest = body;
  while (rest.length > 0) {
    let best: { index: number; id: number; token: string } | null = null;
    for (const t of tokens) {
      const index = rest.indexOf(t.token);
      if (index !== -1 && (best === null || index < best.index)) {
        best = { index, id: t.id, token: t.token };
      }
    }
    if (!best) {
      segments.push(...splitLinks(rest));
      break;
    }
    if (best.index > 0) segments.push(...splitLinks(rest.slice(0, best.index)));
    segments.push({ kind: "mention", text: best.token, userId: best.id });
    rest = rest.slice(best.index + best.token.length);
  }
  return segments;
}
